/**
 * GA4 client for the whole app. Uses the same Vistry GA4 property
 * eva-website already reports to (see eva-website/src/utils/consent.js) so
 * conversions and in-app usage land alongside marketing-site traffic in the
 * same GA4 property.
 *
 * No consent gate: eva-insights-platform has no cookie-consent mechanism
 * (unlike eva-website). Page views fire on every client-side route change,
 * authenticated views included. Whether that needs a consent gate is the open
 * decision on EIP-433; recording it there is the merge gate for this code, and
 * this file does not restate the decision.
 *
 * What GA4 is allowed to see:
 *  - lifecycle events: the ALLOWED_FIELDS allowlist below and
 *    docs/eva-insights-signup-attribution-lifecycle-ticket.md's GA4 section.
 *    Never pass email, name, tenant name, password, or request messages here.
 *  - page views: origin + pathname only, with per-user route params masked
 *    (maskPath). Never the query string: /reset-password, /verify-email,
 *    /accept-invitation, /accept-superadmin-invitation and /signup?d= all
 *    carry single-use tokens there. Every hit this module sends sets
 *    page_location explicitly so gtag never falls back to document.location;
 *    the token pages also strip the query on mount as defence in depth for
 *    gtag's own automatic events (session_start, user_engagement, ...).
 *
 * Only loaded on the production host -- see isTrackedHost.
 */

import { matchPath } from 'react-router';

const GA4_MEASUREMENT_ID = process.env.REACT_APP_GA4_ID || 'G-CCP54HCRET';

// GA4_MEASUREMENT_ID falls back to the live production stream and no
// environment passes REACT_APP_GA4_ID (neither Dockerfile nor
// deploy-reusable.yml), so every build -- dev.insights, stg.insights, the CI
// E2E job (NODE_ENV=production served on localhost:3000) and `yarn start` on a
// laptop -- reported into the one production stream. Found 2026-08-27: 84% of
// the stream's active users over 28 days were hostname=localhost. A positive
// allowlist rather than a localhost denylist, so dev and staging stop polluting
// prod numbers too. Analytics is therefore off everywhere but the branded prod
// host (frontend nginx 308-redirects *.run.app there); verify in prod GA4
// DebugView after promotion, not on staging.
const TRACKED_HOST = 'insights.eva.vistry.ai';

function isTrackedHost() {
  return process.env.NODE_ENV === 'production' && window.location.hostname === TRACKED_HOST;
}

// Dashboard routes whose path segment identifies a person or a tenant (App.js,
// nested under the "/" DashboardLayout route, so the patterns are absolute).
// GA4 derives its "Page path" dimension from page_location, so the mask applies
// to page_location as well as page_path. Keep in sync with the route table when
// a new :param route is added.
const MASKED_ROUTE_PATTERNS = [
  '/people/employees/:userId',
  '/engagement/employees/:userId',
  '/superadmin/tenants/:tenantId',
];

/**
 * Replaces a per-user or per-tenant id in a pathname with its route pattern
 * (/people/employees/42 -> /people/employees/:userId). Unmatched pathnames are
 * returned unchanged.
 */
export function maskPath(pathname) {
  for (const pattern of MASKED_ROUTE_PATTERNS) {
    if (matchPath(pattern, pathname)) return pattern;
  }
  return pathname;
}

// origin + masked pathname of the current document. Never the query or hash.
function currentPageLocation() {
  return window.location.origin + maskPath(window.location.pathname);
}

let loaded = false;

function ensureGtag() {
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtag() {
    window.dataLayer.push(arguments);
  };
}

// Private: App's usePageTracking and the signup pages only ever call
// trackPageView / trackGa4Event. Returns whether gtag is ready for a hit.
function loadGa4() {
  if (loaded) return true;
  if (!GA4_MEASUREMENT_ID || !isTrackedHost()) return false;
  loaded = true;
  try {
    ensureGtag();
    const script = document.createElement('script');
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`;
    script.async = true;
    document.head.appendChild(script);
    window.gtag('js', new Date());
    // send_page_view stays false -- this is an SPA, so pageviews are tracked
    // per route change via trackPageView() instead of once on initial load.
    // page_location is pinned here so the value gtag attaches to its own
    // automatic events is the sanitised one, not document.location. config runs
    // once per tab, so pinDefaultPageLocation below refreshes it on every
    // navigation; without that, every automatic event for the rest of the
    // session would report the route the tab happened to open on.
    window.gtag('config', GA4_MEASUREMENT_ID, {
      send_page_view: false,
      page_location: currentPageLocation(),
    });
    return true;
  } catch {
    // Best-effort -- a failed script load must never break the app.
    return false;
  }
}

// gtag merges what `set` receives into every later hit that does not override
// it, including the automatic ones this module never calls (session_start,
// user_engagement, and Enhanced Measurement's scroll and outbound_click). Those
// carry no page_location of their own, so this is what keeps them on the
// current route and free of the query string.
function pinDefaultPageLocation() {
  window.gtag('set', { page_location: currentPageLocation() });
}

/**
 * Fires a GA4 page_view for a route pathname. Called on initial mount and on
 * every client-side route change (this is an SPA -- the browser never reloads,
 * so GA4's automatic page_view on script load would only ever see the first
 * route). Sends the masked path and origin + masked path; never the query
 * string.
 */
export function trackPageView(pathname) {
  try {
    if (!loadGa4()) return;
    pinDefaultPageLocation();
    window.gtag('event', 'page_view', {
      page_path: maskPath(pathname),
      page_location: currentPageLocation(),
      page_title: document.title,
    });
  } catch {
    // Best-effort.
  }
}

// The only fields GA4 is allowed to see for these events -- mirrors the
// backend lifecycle event names 1:1, but strictly excludes anything that
// could identify a person (email, name, tenant name, messages, errors).
const ALLOWED_FIELDS = [
  'visitor_id',
  'session_id',
  'landing_host',
  'cta',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'signup_flow_type',
  'request_status',
  'tenant_status',
  // Opaque CRM contact row id (not PII -- see signupAttribution.js's own
  // comment on eva_contact_id). Added 2026-08-27: a GA4 "Contact ID" custom
  // dimension already existed but nothing populated it for these events, so
  // a signup_submit_error spike (676 events) had zero attributable identity
  // in GA4 -- the contact had to be found via the backend DB instead. Lets
  // future incidents be traced from GA4 alone.
  'contact_id',
];

/**
 * Fires a GA4 event, keeping only the allowed non-PII fields plus a sanitised
 * page_location. Never throws: a beacon failing must never surface as a
 * signup-page error.
 */
export function trackGa4Event(eventName, fields = {}) {
  try {
    if (!loadGa4()) return;
    pinDefaultPageLocation();
    const params = { page_location: currentPageLocation() };
    ALLOWED_FIELDS.forEach((key) => {
      const value = fields[key];
      if (value !== undefined && value !== null && value !== '') params[key] = value;
    });
    window.gtag('event', eventName, params);
  } catch {
    // Best-effort.
  }
}
